import { Documentation } from 'graphql-common';
import { AttachmentValue } from '@lib/components/FileUploaderUppy/FileUploaderUppy';
import { Values } from '@lib/interfaces/form';
import getFullName from '@lib/utils/getFullName';
import { dateOnlyToDatePickerValue } from '@lib/utils/dateTimeHelpers';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';

export const DEFAULT_VALUES: Values = {
  [DocumentFormFieldNames.Name]: '',
  [DocumentFormFieldNames.Code]: '',
  [DocumentFormFieldNames.Description]: '',
  [DocumentFormFieldNames.Category]: null,
  [DocumentFormFieldNames.Tags]: [],
  [DocumentFormFieldNames.Attachment]: null,
  [DocumentFormFieldNames.Sectors]: [],
  [DocumentFormFieldNames.Sites]: [],
  [DocumentFormFieldNames.Approver]: null,
  [DocumentFormFieldNames.Equipment]: [],
  [DocumentFormFieldNames.Areas]: [],
  [DocumentFormFieldNames.Tasks]: [],
  [DocumentFormFieldNames.FormTemplates]: [],
  [DocumentFormFieldNames.RenewalReminderEnabled]: false,
  [DocumentFormFieldNames.RenewalReminderDeadlineDate]: null,
  [DocumentFormFieldNames.RenewalReminderRepetition]: null,
  [DocumentFormFieldNames.RenewalReminderCustomDays]: null,
  [DocumentFormFieldNames.RenewalReminderEarlyAmount]: null,
};

export type PrefillData = {
  assetId: string;
  assetName?: string;
};

export type LinkedEntityIds = {
  sectorIds: string[];
  siteIds: string[];
  assetIds: string[];
  areaIds: string[];
  formTemplateIds: string[];
  taskIds: string[];
};

const idsToOptions = (ids: string[] | undefined) =>
  (ids || []).map((id) => ({ value: id }));

const getAttachmentValue = (data: Documentation): AttachmentValue | null =>
  data.currentVersion?.attachmentAttached
    ? (data.currentVersion.attachmentAttached as AttachmentValue)
    : null;

export default function getDefaultValuesFromQueryData(
  data: Documentation | undefined,
  prefillData?: PrefillData,
  linkedEntityIds?: LinkedEntityIds,
): Values {
  if (!data?.id) {
    if (prefillData?.assetId) {
      return {
        ...DEFAULT_VALUES,
        [DocumentFormFieldNames.Equipment]: [
          {
            value: prefillData.assetId,
            label: prefillData.assetName || prefillData.assetId,
          },
        ],
      };
    }
    return DEFAULT_VALUES;
  }

  const hasRenewal = !!data.renewalDeadlineDate || !!data.renewalRepetition;

  return {
    ...DEFAULT_VALUES,
    [DocumentFormFieldNames.Name]: data.name ?? '',
    [DocumentFormFieldNames.Code]: data.code ?? '',
    [DocumentFormFieldNames.Description]: data.description ?? '',
    [DocumentFormFieldNames.Category]: data.category
      ? {
          value: data.category.id,
          label: data.category.name,
          color: data.category.color,
        }
      : null,
    [DocumentFormFieldNames.Tags]: (data.tags || []).map(({ id, name }) => ({
      value: id,
      label: name,
    })),
    [DocumentFormFieldNames.Attachment]: getAttachmentValue(data),
    [DocumentFormFieldNames.Approver]: data.approver
      ? {
          value: data.approver.id,
          label: getFullName(data.approver),
          data: data.approver,
        }
      : null,
    [DocumentFormFieldNames.Sectors]: idsToOptions(linkedEntityIds?.sectorIds),
    [DocumentFormFieldNames.Sites]: idsToOptions(linkedEntityIds?.siteIds),
    [DocumentFormFieldNames.Equipment]: idsToOptions(linkedEntityIds?.assetIds),
    [DocumentFormFieldNames.Areas]: idsToOptions(linkedEntityIds?.areaIds),
    [DocumentFormFieldNames.FormTemplates]: idsToOptions(
      linkedEntityIds?.formTemplateIds,
    ),
    [DocumentFormFieldNames.Tasks]: idsToOptions(linkedEntityIds?.taskIds),
    [DocumentFormFieldNames.RenewalReminderEnabled]: hasRenewal,
    [DocumentFormFieldNames.RenewalReminderDeadlineDate]:
      dateOnlyToDatePickerValue(data.renewalDeadlineDate),
    [DocumentFormFieldNames.RenewalReminderRepetition]: data.renewalRepetition
      ? { value: data.renewalRepetition }
      : null,
    [DocumentFormFieldNames.RenewalReminderCustomDays]:
      data.renewalRepetitionCustomDays ?? null,
    [DocumentFormFieldNames.RenewalReminderEarlyAmount]:
      data.renewalEarlyReminderDays ?? null,
  };
}
